import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import moment from 'moment'
import { useSearchParams } from 'react-router-dom'

import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { PRECISE_TIME_FORMAT, TIME_FORMAT } from 'app/constants/date'
import {
  formatDurationInDays,
  formatExtendedDuration,
} from 'app/common/dateHelper'
import { getDemoCompanies, editDemoCompany } from 'app/actions/company'
import { getDemoReport } from 'app/actions/printout'
import ExtendDemoModal from 'app/components/administration/extendDemoModal'
import { DemoCompaniesModal } from '../printout/DemoCompaniesModal'
import { TableProvider } from '@/modules/drivers/context/TableContext'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { AdminIcon } from '@/modules/shared/components'
import { Pagination } from '@/modules/shared/components/Pagination'
import { Button } from '@/ui/button'
import { toast } from '@/lib/toasts/api'
import { AdminColumn, AdminTable, AdminToolbar } from '../shared/AdminTable'

type Props = {
  currentCompanyId?: string
  onEditCompany: (company: CompanyDetailsModel) => void
}

const PER_PAGE = 10

/**
 * Zakładka FIRMY DEMO panelu admina - funkcyjna, na react-query.
 *
 * Zastępuje `app/components/administration/demoCompanies.tsx`. Stronicowanie i sortowanie
 * po stronie serwera; filtr po serwerze/bazie nadal przychodzi z URL-a.
 */
const AdminDemoCompaniesPageContent: React.FC<Props> = ({
  currentCompanyId,
  onEditCompany,
}) => {
  const { t } = useLocalize()
  const [searchParams] = useSearchParams()

  const serverId = Number(searchParams.get('filterByServer') ?? 0)
  const databaseId = Number(searchParams.get('filterByDatabase') ?? 0)

  const [page, setPage] = React.useState(1)
  const [pageSize, setPageSize] = React.useState(PER_PAGE)
  const [orderBy, setOrderBy] = React.useState('createdDate')
  const [descending, setDescending] = React.useState(true)
  const [search, setSearch] = React.useState('')
  const [debounced, setDebounced] = React.useState('')
  const [extended, setExtended] = React.useState<CompanyDetailsModel | null>(
    null,
  )
  const [printoutOpen, setPrintoutOpen] = React.useState(false)

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(search)
      setPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [search])

  const query = useQuery({
    queryKey: [
      'admin',
      'demoCompanies',
      currentCompanyId ?? null,
      pageSize,
      page,
      orderBy,
      descending,
      debounced,
      serverId,
      databaseId,
    ],
    queryFn: () =>
      getDemoCompanies(
        currentCompanyId,
        pageSize,
        pageSize * (page - 1),
        orderBy,
        descending,
        { searchValue: debounced, serverId, databaseId },
      ),
  })

  const companies: CompanyDetailsModel[] = query.data?.companies ?? []
  const total: number = query.data?.companiesCount ?? 0

  const columns: Array<AdminColumn<CompanyDetailsModel>> = [
    {
      key: 'status',
      header: t('administrationPanel-companies.status'),
      width: 70,
      align: 'center',
      render: (company) =>
        company.running ? (
          <AdminIcon
            name="connected"
            title={t('administrationPanel-companies.database-connected')}
          />
        ) : (
          <AdminIcon
            name="not-connected"
            title={
              t('administrationPanel-companies.database-not-connected') +
              (company.lastErrorMessage ? `: ${company.lastErrorMessage}` : '')
            }
          />
        ),
    },
    {
      key: 'companyName',
      header: t('administrationPanel-demoCompanies.machine'),
      render: (company) =>
        company.tenant != null ? (
          company.companyName
        ) : (
          <span className="tw-flex tw-flex-col">
            <span>
              <b>{company.ipAddress}</b>{' '}
              <span className="tw-ml-2">{company.uniqueIdentifier}</span>
            </span>
            {company.companyName !== company.uniqueIdentifier && (
              <span className="tw-font-medium">{company.companyName}</span>
            )}
          </span>
        ),
    },
    {
      key: 'demoKey',
      header: t('administrationPanel-demoCompanies.demoKey'),
      width: 180,
    },
    {
      key: 'subscriptionExpirationDate',
      header: t('administrationPanel-demoCompanies.timeRemaining'),
      width: 220,
      render: (company) =>
        company.tenant != null ? null : (
          <span
            className="tw-flex tw-justify-between tw-gap-2"
            style={{
              color:
                formatDurationInDays(company.demoTimeRemaining) < 1
                  ? '#f00'
                  : undefined,
            }}
          >
            <span>{moment(company.demoUntil).format(TIME_FORMAT)}</span>
            <span className="tw-font-medium tw-italic">
              {formatExtendedDuration(company.demoTimeRemaining, true)}
            </span>
          </span>
        ),
    },
    {
      key: 'createdDate',
      header: t('auditFields.created-date'),
      width: 180,
      sortable: true,
      render: (company) => (
        <span
          style={{
            color: company.deletedDate ? '#f00' : undefined,
            fontWeight: company.deletedDate ? 600 : 400,
          }}
        >
          {company.deletedDate
            ? moment(company.deletedDate).format(PRECISE_TIME_FORMAT)
            : company.createdDate
              ? moment(company.createdDate).format(PRECISE_TIME_FORMAT)
              : ''}
        </span>
      ),
    },
    {
      key: 'databaseVersion',
      header: t('administrationPanel-databases.version'),
      width: 160,
      align: 'right',
    },
    {
      key: 'driversCount',
      header: t('administrationPanel-demoCompanies.driversCount'),
      width: 150,
      align: 'right',
    },
    {
      key: 'lastSynchronizationDate',
      header: t('administrationPanel-companies.last-synchronization-date'),
      width: 200,
      render: (company) =>
        company.lastSynchronizationDate
          ? moment(company.lastSynchronizationDate).format(PRECISE_TIME_FORMAT)
          : '',
    },
    {
      key: 'extend',
      header: '',
      width: 58,
      align: 'center',
      render: (company) =>
        company.tenant != null ? null : (
          <AdminIcon
            name="edit"
            title={t('operation.edit')}
            onClick={(event) => {
              event.stopPropagation()
              setExtended(company)
            }}
          />
        ),
    },
  ]

  return (
    <div className="tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-bg-content-primary">
      <AdminToolbar search={search} onSearch={setSearch}>
        <Button
          variant="primary"
          mode="outline"
          size="sm"
          onClick={() => setPrintoutOpen(true)}
        >
          <AdminIcon name="print" className="tw-h-4 tw-w-4" />
          {t('operation.get-pdf')}
        </Button>
      </AdminToolbar>

      <AdminTable
        idPrefix="demo-company"
        rows={companies}
        columns={columns}
        rowId={(company) => company.id}
        selectable={false}
        isLoading={query.isLoading}
        orderBy={orderBy}
        descending={descending}
        onSort={(key) => {
          setDescending(orderBy === key ? !descending : true)
          setOrderBy(key)
          setPage(1)
        }}
        onRowDoubleClick={onEditCompany}
        footer={
          <div className="tw-flex-shrink-0 tw-border-t tw-border-base-300">
            <Pagination
              currentPage={page}
              totalItems={total}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size)
                setPage(1)
              }}
            />
          </div>
        }
      />

      {extended && (
        <ExtendDemoModal
          name={
            extended.companyName !== extended.uniqueIdentifier
              ? extended.companyName
              : ''
          }
          dateTo={moment(extended.demoUntil)}
          onClose={async (dateTo: any, name: string) => {
            if (dateTo != null) {
              await editDemoCompany(extended.id, dateTo, name)
              toast.success({
                title: t(
                  'administrationPanel-demoCompanies.change-demo-time-title',
                ),
                text: t(
                  'administrationPanel-demoCompanies.change-demo-time-details',
                ).replace('{0}', moment(dateTo).format(TIME_FORMAT)),
              })
              await query.refetch()
            }
            setExtended(null)
          }}
        />
      )}

      {printoutOpen && (
        <DemoCompaniesModal
          getDemoReport={getDemoReport}
          onClose={() => setPrintoutOpen(false)}
        />
      )}
    </div>
  )
}

export const AdminDemoCompaniesPage: React.FC<Props> = (props) => (
  <TableProvider>
    <AdminDemoCompaniesPageContent {...props} />
  </TableProvider>
)
